import { EditOutlined, FileUploadOutlined } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isFunction, isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererLoanFileStatus from "../agGrid/cellRenderer/CellRendererLoanFileStatus";
import BorrowerFilePreviewModal from "../files/BorrowerProfileFilePreview";
import Flx from "../layout/Flx";
import LoanFilePreviewModal from "../modals/files/LoanFilePreviewModal";
import Txt from "../typography/Txt";

ModuleRegistry.registerModules([
  RowGroupingPanelModule,
  RowGroupingModule,
  TreeDataModule,
]);

const BasicFilesTable = ({
  files,
  docGroup,
  docType,
  height = "100%",
  emptyContent,
  emptyMessage,
  simpleTable = true,
  compactTable = false,
  gridOptions,
  borrowerId,
  routeType, // useBorrowerPortalHook

  quickFilterText,
  showFileOptions = false,
  showUploadDate = false,
  rowGroup = null,

  showStatus = false,
  rowStyles,
  hideDocType = false,

  showEditButton = false,
  getRowStyle,
  onEditClick,
  relatedFiles,
  sx,
}) => {
  const [file, setFile] = useState(null);
  // console.log("files", files);
  const columnDefs = useMemo(
    () => [
      {
        headerName: "File Name",
        field: "file_display_name",
        flex: 1,
        cellStyle: {
          paddingLeft: "12px",
          alignItems: "center",
          display: "flex",
        },
        // tooltipValueGetter: (p) =>
        //   p?.data?.isMissing
        //     ? "Missing Document - Click to Upload"
        //     : "Preview File",
        minWidth: 200,
        // cellRenderer: (params) => {
        //   if (defaultView) {
        //     return params.data?.file_display_name;
        //   }
        //   return (
        //     <Flx
        //       ac
        //       gap={1}
        //       sx={{
        //         minWidth: 0,
        //         width: "100%",
        //         svg: {
        //           mt: "3px",
        //         },
        //       }}
        //     >
        //       <FilePreviewImageTypeIcon
        //         size={21}
        //         contentType={params.data?.content_type}
        //       />
        //       <Flx column jc sx={{ minWidth: 0, flex: 1 }}>
        //         <Txt
        //           sx={{
        //             fontWeight: params?.data?.isMissing ? 400 : 500,
        //             fontSize: "11px",
        //             lineHeight: "18px",
        //             overflow: "hidden",
        //             textOverflow: "ellipsis",
        //             whiteSpace: "nowrap",
        //           }}
        //         >
        //           {params.data?.file_display_name}
        //         </Txt>
        //         {hideFileCellDate ? null : (
        //           <Txt secondary sx={{ fontSize: "10px", lineHeight: 1 }}>
        //             {formatDateShort(params.data?.uploadDate)}
        //           </Txt>
        //         )}
        //       </Flx>
        //     </Flx>
        //   );
        // },
      },
      {
        headerName: "Status",
        rowGroup: rowGroup === "documentStatus",
        field: "documentStatus",
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        flex: 0,
        hide: rowGroup === "documentStatus" ? true : !showStatus,
        cellClass: "ag-cell-vertical-center",
        cellStyle: {
          alignItems: "center",
          display: "flex",
        },
        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} />
        ),
      },
      // {
      //   headerName: "Category",
      //   valueGetter: (params) => {
      //     const value = params.data?.docGroup;
      //     return value === undefined || value === null || value === ""
      //       ? "Uncategorized"
      //       : value;
      //   },
      //   rowGroup: rowGroup === "docGroup",
      //   sort: rowGroup === "docGroup" ? "asc" : null,
      //   field: "docGroup",
      //   hide:
      //     rowGroup === "docGroup"
      //       ? true
      //       : isNil(docGroup) && !hideDocGroup
      //         ? false
      //         : true,
      //   comparator: (valueA, valueB) => {
      //     if (valueA === "Uncategorized" && valueB !== "Uncategorized")
      //       return 1;
      //     if (valueA !== "Uncategorized" && valueB === "Uncategorized")
      //       return -1;
      //     return valueA.localeCompare(valueB);
      //   },
      // },
      {
        headerName: "Type",
        valueGetter: (params) => {
          const value = params.data?.docType;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        rowGroup: rowGroup === "docType",
        minWidth: 180,
        flex: 1,
        sort: rowGroup === "docType" ? "asc" : null,
        field: "docType",
        hide:
          rowGroup === "docType"
            ? true
            : isNil(docType) && !hideDocType
              ? false
              : true,
        comparator: (valueA, valueB) => {
          if (valueA === "Uncategorized" && valueB !== "Uncategorized")
            return 1;
          if (valueA !== "Uncategorized" && valueB === "Uncategorized")
            return -1;
          return valueA.localeCompare(valueB);
        },
      },
      {
        headerName: "Upload Date",
        sort: "desc",
        field: "uploadDate",
        valueFormatter: (params) => {
          return formatDateShort(params.value);
        },
        flex: 0,
        hide: !showUploadDate,
      },
      {
        width: 38,
        cellRenderer: CellRendererFileOptions,
        hide: !showFileOptions,
        field: "actions",
        headerName: "",
        pinned: "right",
        cellStyle: {
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          display: "flex",
          cursor: "pointer",
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      {
        width: 40,
        minWidth: 40,
        pinned: "right",
        field: "edit",
        headerName: "",
        flex: 0,
        hide: !showEditButton,
        cellStyle: { padding: 0, cursor: "pointer" },

        cellRenderer: (p) => {
          return (
            <Tooltip title="Edit Document">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pr: 0,
                  flexGrow: 1,
                }}
              >
                <EditOutlined className="thin" />
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [
      docType,
      showFileOptions,
      showStatus,
      hideDocType,
      showUploadDate,
      showEditButton,
      rowGroup,
    ],
  );
  const columnTypes = useColumnTypes();

  const getRowId = useCallback((params) => {
    return params.data._id;
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      cellClass: "ag-cell-vertical-center",
    };
  }, []);

  const onCellClicked = useCallback((params) => {
    if (onEditClick && params?.colDef?.field === "edit") {
      onEditClick(params?.data);
      return;
    }
    setFile(params.data);
  }, []);

  const onRowClicked = useCallback((params) => {
    const clickedElement = params.event?.target;
    // console.log("params.event?.target", params);
    if (
      (clickedElement instanceof Element && clickedElement.closest("button")) ||
      (clickedElement instanceof Element && clickedElement.closest("a"))
    ) {
      return;
    }
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
      return;
    }
  }, []);

  const styles = useMemo(() => {
    return {
      position: "relative",
      flexGrow: 1,
      p: 0,
      overflow: "hidden",
      ...sx,
    };
  }, [sx]);

  const getRowStyles = useCallback(
    (params) => {
      if (isFunction(getRowStyle)) {
        return getRowStyle(params);
      }
      const renderedRowStyles = isFunction(rowStyles) ? rowStyles(params) : {};
      if (params.node.group) {
        return {
          fontWeight: "600",
          backgroundColor: grey[50],
          cursor: "pointer",
          borderBottom: `1px solid ${grey[200]}`,
          // ...renderedRowStyles,
        };
      }
      return { ...renderedRowStyles };
    },
    [getRowStyle, rowStyles],
  );

  if ((emptyMessage || emptyContent) && isEmpty(files)) {
    if (emptyContent) {
      return emptyContent;
    }
    return (
      <Txt secondary sx={{ p: 1 }}>
        {emptyMessage}
      </Txt>
    );
  }

  return (
    <>
      <AgGridTableWrapper
        height={height}
        suppressMinHeightWhenPopulated={files}
        simpleTable={compactTable ? false : simpleTable}
        compactTable={compactTable}
        sx={styles}
      >
        <AgGridReact
          rowData={files}
          columnDefs={columnDefs}
          // onFirstDataRendered={onFirstDataRendered}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          onCellClicked={onCellClicked}
          onRowClicked={onRowClicked}
          // headerHeight={headerHeight}
          columnTypes={columnTypes}
          getRowId={getRowId}
          getRowStyle={getRowStyles}
          groupDisplayType={"groupRows"}
          groupDefaultExpanded={1}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          tooltipMouseTrack
          enableCellTextSelection
          gridOptions={gridOptions}
        />
      </AgGridTableWrapper>

      {isNil(routeType) && (
        <LoanFilePreviewModal
          file={file}
          relatedFiles={relatedFiles}
          open={!isNil(file)}
          containerId={file?.containerName}
          onClose={() => setFile(null)}
          onSelectRelatedFile={setFile}
        />
      )}
      {routeType === "useBorrowerPortalHook" && borrowerId && (
        <BorrowerFilePreviewModal
          type={routeType}
          fileObject={file}
          open={!isNil(file)}
          containerId={file?.containerName}
          borrowerId={borrowerId}
          onClose={() => setFile(null)}
        />
      )}
      {/* {isNil(routeType) && (
        <FilePreviewSideModal
          type={routeType}
          file={file}
          onClose={() => setFile(null)}
        />
      )}
      {routeType === "useBorrowerPortalHook" && borrowerId && (
        <BorrowerFilePreviewModal
          type={routeType}
          fileObject={file}
          open={file}
          containerId={file?.containerName}
          borrowerId={borrowerId}
          onClose={() => setFile(null)}
        />
      )} */}
    </>
  );
};

const CellRendererFileOptions = (params) => {
  const file = useMemo(() => params?.data, [params?.data]);

  let content = null;
  if (params?.data?.isMissing) {
    content = (
      <Tooltip title={`Upload ${file.docType}`}>
        <FileUploadOutlined className="thin" />
      </Tooltip>
    );
  } else {
    content = (
      <Tooltip title="Edit File">
        <EditOutlined className="thin" />
      </Tooltip>
    );
  }

  return (
    <Flx sx={{ height: "100%" }} center>
      {content}
    </Flx>
  );
};

export default BasicFilesTable;
